/* @flow */

import * as React from 'react';

import classNames from 'classnames';
import {AnimatePresence, motion} from 'framer-motion';

import {colors} from '../colors';
import {LoadingIcon} from '../icon';
import './saved-setting-toast.module.css';

export type SavedSettingToastState = 'saving' | 'complete' | 'initial';

type Props = {
    children?: React.Node,
    state: SavedSettingToastState,
    toastStyles?: Object,
    savedText: string,
    onStateChange?: (newState: SavedSettingToastState) => void,
    autoResetDelay?: number,
    largerSpinner?: boolean,
};

const spinnerVariants = {
    visible: {opacity: 1},
    hidden: {opacity: 0},
};

export function SavedSettingToast(props: Props) {
    const timeoutRef = React.useRef<?TimeoutID>(null);

    React.useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        if (props.state === 'complete' && props.onStateChange) {
            timeoutRef.current = setTimeout(() => {
                if (props.onStateChange) {
                    props.onStateChange('initial');
                }
                timeoutRef.current = null;
            }, props.autoResetDelay);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.state, props.onStateChange]);

    React.useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, []);

    const spinnerStyleNames = classNames({
        spinner: !props.largerSpinner,
        'larger-spinner': props.largerSpinner,
    });

    return (
        <div className='flex align-center'>
            {props.children}
            <motion.div
                styleName={spinnerStyleNames}
                style={props.toastStyles}
                aria-hidden={true}
                initial='hidden'
                animate={props.state === 'saving' ? 'visible' : 'hidden'}
                variants={spinnerVariants}
                transition={{duration: 0.2}}
            >
                <LoadingIcon size={null} wrapWithDiv={false} fill={colors.green} />
            </motion.div>
            <AnimatePresence>
                {props.state === 'complete' && (
                    <motion.div
                        key='complete'
                        styleName='save-complete'
                        style={props.toastStyles}
                        initial={{y: 0, x: props.children ? -10 : -5, opacity: 0}}
                        animate={{y: 0, x: props.children ? -5 : -10, opacity: 1}}
                        exit={{y: -10, x: props.children ? -5 : -10, opacity: 0}}
                        transition={{duration: 0.2}}
                    >
                        {props.savedText}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

SavedSettingToast.defaultProps = {
    savedText: 'Saved',
    autoResetDelay: 2000,
};
